import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, MapPin, ShieldCheck, Users, Gem } from "lucide-react";
import { CATEGORIES } from "@/data/constants";
import CategoryIcon from "@/components/CategoryIcon";
import { api } from "@/lib/api";
import VenueCard from "@/components/VenueCard";

const HERO_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_accessible-swindon/artifacts/kfr6aaty_67281798-828c-4cd5-b668-d7fd46b664f6.webp";

export default function Home() {
  const [diamond, setDiamond] = useState([]);
  useEffect(() => {
    api.get("/venues?min_tier=diamond&limit=6").then(r => setDiamond(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      {/* Hero */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-blue-700 bg-blue-50 border border-blue-100 px-3 py-1 rounded-full">
              <MapPin className="w-3.5 h-3.5" /> Swindon · Wiltshire · Expanding soon
            </span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05]">
              Find places that welcome <span className="text-blue-700">everyone.</span>
            </h1>
            <p className="mt-5 text-lg text-gray-700 leading-relaxed max-w-xl">
              Access Local ranks and showcases the most accessible shops, cafés, workplaces and attractions — inspected on the ground and trusted by our community.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-6" data-testid="hero-search-btn">
                <Link to="/search">Browse accessible places <ArrowRight className="w-4 h-4 ml-2"/></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-2 border-gray-900 h-12 px-6" data-testid="hero-nominate-btn">
                <Link to="/nominate">Nominate a place</Link>
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600"/> Inspected on-site</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-600"/> Community nominated</div>
              <div className="flex items-center gap-2"><Gem className="w-4 h-4 text-indigo-600"/> Bronze → Diamond grading</div>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border-2 border-gray-900 shadow-sm">
              <img src={HERO_IMG} alt="Coate Water Country Park, Swindon — the iconic diving platform above the lake" className="w-full h-[420px] object-cover"/>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border-2 border-gray-900 rounded-xl p-4 max-w-[220px]">
              <div className="text-xs font-semibold uppercase tracking-widest text-blue-700">Trusted grading</div>
              <div className="mt-1 font-heading text-lg font-semibold">Six categories, one honest score.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Diamond spotlight */}
      {diamond.length > 0 && (
        <section className="bg-white border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-indigo-800 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full">
                  <Gem className="w-3.5 h-3.5"/> Diamond graded
                </span>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold text-gray-900">The highest-graded places in our community</h2>
                <p className="mt-2 text-gray-700 max-w-2xl">Venues scoring 81+ across a full accessibility inspection.</p>
              </div>
              <Button asChild variant="outline" className="rounded-full border-2 border-gray-900" data-testid="see-all-diamond">
                <Link to="/search?min_tier=diamond">See all Diamond places <ArrowRight className="w-4 h-4 ml-1"/></Link>
              </Button>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {diamond.map(v => <VenueCard key={v.id} venue={v} />)}
            </div>
          </div>
        </section>
      )}

      {/* Categories bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-gray-900">Six categories, real world tested.</h2>
          <p className="mt-4 text-lg text-gray-700 leading-relaxed">Every venue is nominated by a community member and independently inspected against a shared accessibility framework.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c, i) => (
            <Link key={c.slug} to={`/search?category=${c.slug}`} data-testid={`cat-card-${c.slug}`} className="group">
              <Card className={`bg-white rounded-xl border border-gray-200 hover:border-gray-900 transition-colors duration-200 h-full ${i === 0 ? "md:col-span-2 lg:col-span-1" : ""}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gray-900 text-white flex items-center justify-center">
                    <CategoryIcon slug={c.slug} className="w-6 h-6" />
                  </div>
                  <h3 className="mt-4 font-heading text-xl font-semibold text-gray-900">{c.short}</h3>
                  <p className="mt-2 text-gray-700">{c.blurb}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-blue-700 font-medium group-hover:gap-2 transition-all duration-200">Explore <ArrowRight className="w-4 h-4"/></div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works strip */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-3 gap-10">
          {[
            { n: "01", t: "Members nominate", d: "Anyone in our community can nominate a place they know is accessible — or not." },
            { n: "02", t: "Inspectors grade", d: "Trained inspectors visit and grade against a clear, consistent framework." },
            { n: "03", t: "Everyone benefits", d: "You get a trusted rating, opening hours and access features before you go." },
          ].map(s => (
            <div key={s.n}>
              <div className="font-heading text-6xl font-bold text-blue-400">{s.n}</div>
              <h3 className="mt-3 font-heading text-2xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-gray-300 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-gray-900">Help build a more welcoming Swindon & Wiltshire.</h2>
        <p className="mt-4 text-lg text-gray-700">Join as a member to nominate places, or become an inspector to help grade them.</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg" className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-6" data-testid="cta-join-btn"><Link to="/register">Join the community</Link></Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-2 border-gray-900 h-12 px-6"><Link to="/how-it-works">Learn how it works</Link></Button>
        </div>
      </section>
    </div>
  );
}
